import { Check } from "@mui/icons-material";
import {
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import React, { useRef } from "react";
import { useIntl } from "react-intl";

import { SelectableItem } from "../../types/SelectableItem";
import { NestedInput } from "../shared/NestedInput/NestedInput";
import { styles } from "./Checkboxes.style";

export interface CheckboxesProps {
  items: SelectableItem[];
  selected: Array<string | number>;
  customValue?: string;
  onSelectedChange: (selected: Array<string | number>) => void;
  onCustomValueChange?: (customValue: string) => void;
}

export const Checkboxes: React.FC<CheckboxesProps> = ({
  items,
  selected,
  onSelectedChange,
  customValue = null,
  onCustomValueChange,
}) => {
  const { formatMessage } = useIntl();

  const inputResetRef = useRef(() => {});

  const handleItemSelect = (selectedItem: SelectableItem) => {
    let updatedSelected;
    const isUnsure =
      selectedItem.value.toLowerCase().includes("unsure") ||
      selected.map((elem) => elem.toString().toLowerCase()).includes("unsure");
    const isDeselecting = selected.includes(selectedItem.value);
    // If patient is unsure, do not allow further data entry, and deselect previous values
    if (isUnsure && !isDeselecting) {
      updatedSelected = [selectedItem.value];
      inputResetRef.current();
    } else {
      if (isDeselecting) {
        updatedSelected = selected.filter(
          (value) => value !== selectedItem.value
        );
      } else {
        updatedSelected = [...selected, selectedItem.value];
      }
    }

    onSelectedChange(updatedSelected);

    const customInput = items.find(({ isCustom }) => isCustom);
    const isCustomSelected = updatedSelected.includes(selectedItem.value);
    if (customInput && customValue && !isCustomSelected) {
      onCustomValueChange && onCustomValueChange("");
    }
  };

  const resetInputState = () => {
    inputResetRef.current();
  };

  return (
    <List component="div">
      {items.map((item) => {
        const isSelected = selected.includes(item.value);
        const selectedIcon = isSelected && (
          <ListItemIcon>
            <Check
              aria-hidden="true"
              color="primary"
            />
          </ListItemIcon>
        );

        return (
          <ListItemButton
            key={item.key}
            selected={isSelected}
            role="checkbox"
            aria-checked={isSelected}
            onClick={() => {
              item.isCustom && resetInputState();
              handleItemSelect(item);
            }}
            sx={styles}
          >
            <div>
              <ListItemText
                primary={item.value}
                secondary={item.hint}
              />
              <NestedInput
                customStrings={{
                  label:
                    item.customDataLabel ??
                    formatMessage({ id: "checkboxes.customInput.label" }),
                  requiredError: formatMessage({
                    id: "checkboxes.customInput.errors.required",
                  }),
                }}
                resetInputState={(callback: () => void) =>
                  (inputResetRef.current = callback)
                }
                item={item}
                isSelected={isSelected}
                customValue={customValue}
                onCustomValueChange={onCustomValueChange}
                onSelectedChange={() => handleItemSelect(item)}
              />
            </div>
            {selectedIcon}
          </ListItemButton>
        );
      })}
    </List>
  );
};
